<template>
    <div v-if="Array.isArray(items)" :class="{ 'no-focus-effects': noFocusEffects }">
        <v-autocomplete
            id="if65Select"
            v-model="model"
            :error-messages="modelErrors"
            :hint="hint"
            :items="items"
            :item-text="itemText"
            :item-value="itemValue"
            :label="label"
            :loading="isLoading"
            :multiple="multiple"
            :placeholder="placeholder"
            :search-input.sync="search"
            :prepend-icon="icon"
            :disabled="disabled"
            hide-no-data
            chips
            :dark="dark"
            :dense="dense"
            deletable-chips
            :flat="flat || noFocusEffects"
            :return-object="returnObject"
            hide-selected
            persistent-hint
            @input="$v.model.$touch()"
            @blur="$v.model.$touch()"
            @focus="$emit('focus', $event)"
            @click="$emit('click', $event)"
        >
        </v-autocomplete>
    </div>
</template>

<script>

import _ from "lodash";
import {validationMixin} from "vuelidate";
import {required} from "vuelidate/lib/validators";

export default {
    mixins: [validationMixin],
    validations: {
        model: {required}
    },
    props: {
        api: {
            type: Function,
            required: true
        },
        clearable: {
            type: Boolean,
            default: true
        },
        dense: {
            type: Boolean,
            default: true
        },
        extraItems: {
            // [{value: 'xxx', text: 'The xxx choice'}, {}, ...]
            default: () => {
                return []
            }
        },
        extraParams: {
            type: Object,
            default: () => {
                return {}
            }
        },
        flat: {
            type: Boolean,
            default: false
        },
        icon: {
            type: String,
        },
        hint: {
            type: String,
            default: ''
        },
        itemText: {
            type: String,
            default: 'text'
        },
        itemValue: {
            type: String,
            default: 'value'
        },
        label: {
            type: String,
            required: true
        },
        multiple: {
            type: Boolean,
            default: false
        },
        nullItem: {
            default: () => {
                return {value: null, text: ''}
            }
        },
        placeholder: {
            type: String,
            default: 'Ricerca'
        },
        required: {
            type: Boolean,
            default: false
        },
        dark: {
          type: Boolean,
          default: false
        },
        value: {
            required: true
        },
        disabled: {
          type: Boolean,
          default: false
        },
        noFocusEffects: {
          type: Boolean,
          default: false
        },
        returnObject: {
          type: Boolean,
          default: false
        }
    },
    data() {
        return {
            descriptionLimit: 60,
            isLoading: true,
            items: [], // Inizializza come array vuoto invece di null per mostrare subito l'autocomplete
            model: null,
            search: '',
            searchTimeout: null, // Per il debounce della ricerca
        }
    },
    computed: {
        modelErrors() {
            const errors = [];
            if (this.required && this.$v.model.$dirty && !this.$v.model.required) {
                errors.push('Campo richiesto');
            }
            return errors;
        }
    },
    mounted() {
        this.loadItems()
    },
    beforeDestroy() {
        // Pulisce il timeout per evitare memory leaks
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    },
    watch: {
        value: {
            handler() {
                this.model = this.value
            },
            immediate: true
        },
        model() {
            this.$emit('input', this.model ? this.model : this.nullItem.value)
        },
        search() {
            this.isLoading = true;

            // Debounce per evitare troppe chiamate API
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            this.searchTimeout = setTimeout(() => {
                this.loadItems();
            }, 300); // 300ms di delay
        },
    },
    methods: {
        loadItems() {
            // Controllo se l'API è disponibile
            if (!this.api || typeof this.api !== 'function') {
                console.warn('API non disponibile per autocomplete:', this.label);
                this.isLoading = false;
                if (!Array.isArray(this.items)) {
                    this.items = []
                }
                return Promise.resolve();
            }

            // Prepara parametri per ricerca server-side
            const params = Object.assign(this.extraParams, {choices: ''})

            // Aggiungi il termine di ricerca ai parametri se presente
            if (this.search && this.search.trim()) {
                params.search = this.search.trim();
                console.log('🔍 DEBUG Autocomplete server-side search:', {
                    label: this.label,
                    searchTerm: this.search.trim()
                });
            }

            // Se items non è ancora stato inizializzato, imposta array vuoto per mostrare l'autocomplete
            if (!Array.isArray(this.items)) {
                this.items = []
            }

            return this.api(params)
                .then(
                    response => {
                        this.$emit('progress', false)
                        const items = _.cloneDeep(response.data)
                        console.log('🔍 DEBUG Autocomplete:', {
                            label: this.label,
                            itemText: this.itemText,
                            itemValue: this.itemValue,
                            rawResponse: response.data,
                            firstItem: response.data?.[0],
                            processedItems: items
                        });
                        for (const extraItem of this.extraItems) {
                            items.push(extraItem)
                        }
                        this.items = items;
                        this.$emit('items-loaded', items)
                    }
                )
                .catch(error => {
                    console.error('Errore nel caricamento autocomplete:', {
                        label: this.label,
                        error: error.message,
                        status: error.response?.status
                    })
                    // Se c'è un errore, mantieni array vuoto per preservare il label
                    if (!Array.isArray(this.items)) {
                        this.items = []
                    }
                    this.$emit('progress', false)
                })
                .finally(() => {
                    this.isLoading = false
                })
        }
    },
}
</script>

<style scoped>
/* Rimuove TUTTI i bordi (superiore, laterali) */
.no-focus-effects >>> .v-text-field.v-text-field--enclosed .v-input__slot,
.no-focus-effects >>> .v-text-field.v-text-field--enclosed .v-input__slot::before,
.no-focus-effects >>> .v-text-field.v-text-field--enclosed .v-input__slot::after {
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  box-shadow: none !important;
}

/* Mantiene SOLO il bordo inferiore (underline) */
.no-focus-effects >>> .v-text-field.v-text-field--enclosed .v-input__slot::after {
  border-bottom: 1px solid rgba(0, 0, 0, 0.42) !important; /* Colore default di Vuetify */
  transform: none !important; /* Evita animazioni */
}

/* Personalizza il colore del bordo inferiore in focus (opzionale) */

</style>
